import { useState } from "react";
import { IconCircle, IconCircleFilled, IconLock } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { skills as allSkills } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import {
  MAX_SKILL_LEVEL,
  SKILL_UPGRADE_COST,
  canUpgradeSkill,
  isSkillUnlocked,
} from "../../engine/skills";
import { skillCategoryText } from "../../i18n/enumText";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { MiniEffectDiagram } from "../tools/MiniEffectDiagram";
import { SkillUpgradeModal } from "./SkillUpgradeModal";
import styles from "./SkillDetails.module.css";

const LEVELS = Array.from({ length: MAX_SKILL_LEVEL }, (_, i) => i + 1);

export function SkillDetails(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const activeSkillId = useGameStore((state) => state.activeSkillId);
  const player = useGameStore((state) => state.player);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const setActiveSkill = useGameStore((state) => state.setActiveSkill);
  const upgradeSkill = useGameStore((state) => state.upgradeSkill);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const skill = activeSkillId ? allSkills.find((s) => s.id === activeSkillId) : null;

  if (!skill) {
    return (
      <Panel title={<Trans>Навык</Trans>}>
        <EmptyState
          title={<Trans>Навык не выбран</Trans>}
          action={
            <Button
              variant="ghost"
              size="small"
              onClick={() => {
                setActiveSkill(null);
                navigateTo("skills");
              }}
            >
              <Trans>Назад</Trans>
            </Button>
          }
        />
      </Panel>
    );
  }

  const level = player.skills[skill.id] ?? 0;
  const maxed = level >= MAX_SKILL_LEVEL;
  const canUpgrade = canUpgradeSkill(player.skills, skill.id, player.skillPoints);
  const isUpcoming = skill.status === "upcoming";
  const unlocked = isSkillUnlocked(skill.minLevel, player.level);
  const unlockLevel = skill.minLevel;

  return (
    <Panel title={t(skill.name)}>
      <div className={styles.back}>
        <Button
          variant="ghost"
          size="small"
          onClick={() => {
            setActiveSkill(null);
            navigateTo("skills");
          }}
        >
          <Trans>← К навыкам</Trans>
        </Button>
      </div>
      <div className={styles.body}>
        <div className={styles.meta}>
          <span className={styles.category}>
            {i18n._(skillCategoryText(skill.category))}
          </span>
          <span className={styles.dots} aria-hidden="true">
            {LEVELS.map((lvl) =>
              lvl <= level ? (
                <IconCircleFilled key={lvl} size={12} className={styles.on} />
              ) : (
                <IconCircle key={lvl} size={12} className={styles.off} />
              ),
            )}
          </span>
        </div>
        <Card>
          <p className={styles.description}>{t(skill.description)}</p>
        </Card>
        {skill.effects.length > 0 && (
          <div className={styles.effectList}>
            <span className={styles.effectListLabel}>
              <Trans>Эффекты по уровням</Trans>
            </span>
            {LEVELS.map((lvl) => {
              const effect = skill.effects[lvl - 1];
              if (!effect) return null;
              const isCurrent = lvl === level;
              const isNext = lvl === level + 1;
              return (
                <div
                  key={lvl}
                  className={`${styles.effectRow}${isCurrent ? ` ${styles.current}` : isNext ? ` ${styles.next}` : ""}`}
                >
                  <span className={styles.levelLabel} aria-hidden="true"><Trans>Lv{lvl}</Trans></span>
                  <span>{t(effect)}</span>
                  {isCurrent && <span className={styles.marker}>← <Trans>сейчас</Trans></span>}
                  {isNext && <span className={styles.marker}>← <Trans>дальше</Trans></span>}
                </div>
              );
            })}
          </div>
        )}
        <MiniEffectDiagram category={skill.category} />
        <div className={styles.cta}>
          {isUpcoming ? (
            <span className={styles.upcomingBadge}>
              <Trans>Скоро</Trans>
              {skill.upcomingType ? ` · ${t(skill.upcomingType)}` : null}
            </span>
          ) : !unlocked ? (
            <span className={styles.lockCond}>
              <IconLock size={14} aria-hidden="true" />
              <Trans>Откроется на ур. {unlockLevel}</Trans>
            </span>
          ) : maxed ? (
            <span className={styles.maxedBadge}>
              <Trans>Макс</Trans>
            </span>
          ) : (
            <Button
              variant="primary"
              size="small"
              disabled={!canUpgrade}
              onClick={() => setConfirmOpen(true)}
            >
              <Trans>Прокачать ({SKILL_UPGRADE_COST} SP)</Trans>
            </Button>
          )}
        </div>
      </div>
      {confirmOpen && (
        <SkillUpgradeModal
          skill={skill}
          currentLevel={level}
          skillPoints={player.skillPoints}
          onConfirm={() => {
            upgradeSkill(skill.id);
            setConfirmOpen(false);
          }}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Panel>
  );
}
